// ─────────────────────────────────────────────────────────────────────────────
// Marca de "acaba de iniciar sesión" — aterrizaje único tras el login.
//
// Regla de negocio (2026-10-09): al iniciar sesión, SIN IMPORTAR el método (Frontline con contraseña,
// SSO de Sigma interactivo o SSO silencioso), la persona aterriza en /app/brillamos si tiene campaña
// Brillamos activa y, si no, en /app/reconocer. Ver `PostLoginGate` (router/PostLoginGate.tsx).
//
// El login termina SIEMPRE en una carga completa de documento (`window.location.assign` en el
// Frontline; el redirect de Entra en el SSO), así que el aviso tiene que sobrevivir a la recarga:
// de ahí `sessionStorage`. Se usa sessionStorage y no localStorage a propósito: la marca es del TAB
// que hizo el login, y una marca huérfana no debe disparar un redirect en otra pestaña ni días
// después. `PostLoginGate` la consume una sola vez, así que recargar la página (F5) ya no redirige.
// ─────────────────────────────────────────────────────────────────────────────

const POST_LOGIN_KEY = 'pmp.postLoginLanding';

/** Deja anotado que este tab acaba de iniciar sesión. Llamar justo antes de la carga completa. */
export function markPostLogin(): void {
  try {
    sessionStorage.setItem(POST_LOGIN_KEY, '1');
  } catch {
    /* storage no disponible (modo privado): sin marca, el login cae en la ruta por defecto */
  }
}

const TEAMS_ENTRY_KEY = 'pmp.teamsEntryDone';

/** Rutas con las que el tab de Teams se abre "sin destino propio": la raíz de la app y la de /app. */
const TEAMS_LANDING_PATHS: readonly string[] = ['/', '/app'];

/**
 * Teams no tiene un login interactivo: el tab abre la app y el SSO del SDK identifica a la persona
 * sin pantalla de por medio. Su "entrada" cuenta como inicio de sesión y aplica la misma regla
 * (Brillamos con campaña activa, si no Reconocer).
 *
 * Dos salvedades, a propósito:
 * - UNA vez por sesión del tab (`TEAMS_ENTRY_KEY`): un F5 o un remonte dentro de Teams no es un
 *   inicio de sesión nuevo y no debe sacar a la persona de donde estaba.
 * - Solo si el tab abre en la raíz o en `/app` (`entryPath`, ya sin el prefijo del ambiente): un
 *   deep-link —la notificación que abre `/app/...?reco=`, un marcador a una vista concreta— tiene
 *   destino propio y no se pisa; el redirect además descartaría su query.
 */
export function markPostLoginOnTeamsEntry(entryPath: string): void {
  try {
    if (sessionStorage.getItem(TEAMS_ENTRY_KEY) === '1') return;
    sessionStorage.setItem(TEAMS_ENTRY_KEY, '1');
  } catch {
    return; // sin storage no hay forma de acotarlo a una vez: mejor no redirigir nunca
  }
  const normalized = entryPath.length > 1 && entryPath.endsWith('/') ? entryPath.slice(0, -1) : entryPath;
  if (TEAMS_LANDING_PATHS.includes(normalized)) markPostLogin();
}

export function hasPostLoginPending(): boolean {
  try {
    return sessionStorage.getItem(POST_LOGIN_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPostLogin(): void {
  try {
    sessionStorage.removeItem(POST_LOGIN_KEY);
  } catch {
    /* no-op */
  }
}
